import type Konva from 'konva';
import { useMemo, useRef } from 'react';
import { Line, Rect } from 'react-konva';
import { useShallow } from 'zustand/react/shallow';
import { uniformDividers } from '~/features/grid/autodetect';
import { getCellRects } from '~/features/grid/cellRects';
import { MIN_CROP_SIZE, MIN_GRID_GAP } from '~/features/grid/constants';
import { resizeCropEdge } from '~/features/grid/cropEdge';
import { selectGridBounds, useColDividers, useRowDividers } from '~/features/grid/selectors';
import { clamp } from '~/lib/utils';
import { useStore } from '~/store';
import { setStageCursor } from './cursors';
import type { CropSide } from './slice';
import { snapAxis } from './snapAxis';

const GRID_COLOR = 'rgba(108, 91, 158, 0.65)';
const GRID_SEL_COLOR = '#FFB300';
const HANDLE_HIT = 14;

type Axis = 'x' | 'y';

/**
 * In non-individual mode, dividers reflow uniformly between fixed crop edges.
 * Dragging an interior divider therefore can't shift just that one — instead
 * we read the dragged position as a hint about desired cell pitch and resolve
 * to the count that places this divider closest to where the user dropped it.
 */
function uniformCountFromDrag(start: number, span: number, index: number, dropped: number): number {
  const ratio = (dropped - start) / span;
  if (ratio <= 0) return 1;
  return Math.max(1, Math.round(index / ratio));
}

type CropEdgeProps = { side: CropSide };

function CropEdge({ side }: CropEdgeProps) {
  const bounds = useStore(selectGridBounds);
  const sel = useStore((s) => s.editor.selectedEdge);
  const drawMode = useStore((s) => s.editor.drawMode);
  const setSelection = useStore((s) => s.setSelection);
  const setCropRect = useStore((s) => s.setCropRect);
  const dims = useStore((s) => s.source.dimensions);

  if (!bounds || !dims) return null;

  const isVertical = side === 'left' || side === 'right';
  const x = side === 'right' ? bounds.x + bounds.w : bounds.x;
  const y = side === 'bottom' ? bounds.y + bounds.h : bounds.y;
  const points = isVertical ? [0, 0, 0, bounds.h] : [0, 0, bounds.w, 0];
  const cursor = isVertical ? 'ew-resize' : 'ns-resize';

  const isSelected = sel?.kind === 'cropEdge' && sel.side === side;
  const interactive = drawMode === 'none';

  const onDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    const node = e.target;
    if (side === 'left') {
      const edge = clamp(0, bounds.x + bounds.w - MIN_CROP_SIZE, snapAxis(node.x(), 'x'));
      node.x(edge);
      node.y(y);
      setCropRect(resizeCropEdge(bounds, dims, side, Math.round(edge)));
      return;
    }
    if (side === 'right') {
      const edge = clamp(bounds.x + MIN_CROP_SIZE, dims.w, snapAxis(node.x(), 'x'));
      node.x(edge);
      node.y(y);
      setCropRect(resizeCropEdge(bounds, dims, side, Math.round(edge)));
      return;
    }
    if (side === 'top') {
      const edge = clamp(0, bounds.y + bounds.h - MIN_CROP_SIZE, snapAxis(node.y(), 'y'));
      node.x(x);
      node.y(edge);
      setCropRect(resizeCropEdge(bounds, dims, side, Math.round(edge)));
      return;
    }
    const edge = clamp(bounds.y + MIN_CROP_SIZE, dims.h, snapAxis(node.y(), 'y'));
    node.x(x);
    node.y(edge);
    setCropRect(resizeCropEdge(bounds, dims, side, Math.round(edge)));
  };

  return (
    <Line
      x={x}
      y={y}
      points={points}
      stroke={isSelected ? GRID_SEL_COLOR : GRID_COLOR}
      strokeWidth={isSelected ? 4 : 2}
      strokeScaleEnabled={false}
      hitStrokeWidth={HANDLE_HIT}
      draggable={interactive}
      listening={interactive}
      onMouseEnter={(e) => setStageCursor(e, cursor)}
      onMouseLeave={(e) => setStageCursor(e, '')}
      onClick={() => setSelection({ kind: 'cropEdge', side })}
      onTap={() => setSelection({ kind: 'cropEdge', side })}
      onDragStart={() => useStore.temporal.getState().pause()}
      onDragMove={onDragMove}
      onDragEnd={() => useStore.temporal.getState().resume()}
    />
  );
}

type DividerProps = {
  axis: Axis;
  index: number;
  /** range orthogonal to `axis` (yMin..yMax for col, xMin..xMax for row) */
  spanMin: number;
  spanMax: number;
};

function Divider({ axis, index, spanMin, spanMax }: DividerProps) {
  const isCol = axis === 'x';
  const cols = useColDividers();
  const rows = useRowDividers();
  const setColDividers = useStore((s) => s.setColDividers);
  const setRowDividers = useStore((s) => s.setRowDividers);
  const setSelection = useStore((s) => s.setSelection);
  const indivCols = useStore((s) => s.grid.individualCols);
  const indivRows = useStore((s) => s.grid.individualRows);
  const sel = useStore((s) => s.editor.selectedEdge);
  const drawMode = useStore((s) => s.editor.drawMode);

  const dividers = isCol ? cols : rows;
  const setDividers = isCol ? setColDividers : setRowDividers;
  const individual = isCol ? indivCols : indivRows;
  const kind = isCol ? 'colDivider' : 'rowDivider';
  const isSelected = sel?.kind === kind && sel.index === index;
  const startRef = useRef<{ start: number; span: number } | null>(null);
  const dividerAtIndex = dividers[index];
  const firstDivider = dividers[0];
  const lastDivider = dividers.at(-1);
  if (dividerAtIndex === undefined || firstDivider === undefined || lastDivider === undefined) {
    return null;
  }

  const points = isCol ? [0, spanMin, 0, spanMax] : [spanMin, 0, spanMax, 0];
  const pos = isCol ? { x: dividerAtIndex, y: 0 } : { x: 0, y: dividerAtIndex };

  const readAxis = (node: Konva.Node) => (isCol ? node.x() : node.y());
  const writeAxis = (node: Konva.Node, v: number) => (isCol ? node.x(v) : node.y(v));
  const writeOrthogonal = (node: Konva.Node) => (isCol ? node.y(0) : node.x(0));

  return (
    <Line
      points={points}
      x={pos.x}
      y={pos.y}
      stroke={isSelected ? GRID_SEL_COLOR : GRID_COLOR}
      strokeWidth={isSelected ? 4 : 2}
      strokeScaleEnabled={false}
      hitStrokeWidth={HANDLE_HIT}
      draggable={drawMode === 'none'}
      listening={drawMode === 'none'}
      onMouseEnter={(e) => setStageCursor(e, isCol ? 'col-resize' : 'row-resize')}
      onMouseLeave={(e) => setStageCursor(e, '')}
      onClick={() => setSelection({ kind, index })}
      onTap={() => setSelection({ kind, index })}
      onDragStart={() => {
        useStore.temporal.getState().pause();
        startRef.current = { start: firstDivider, span: lastDivider - firstDivider };
      }}
      onDragMove={(e) => {
        const node = e.target;
        writeOrthogonal(node);
        const snap = startRef.current;
        let lo = (snap?.start ?? 0) + MIN_GRID_GAP;
        let hi = (snap?.start ?? 0) + (snap?.span ?? 0) - MIN_GRID_GAP;
        if (individual) {
          const prevDivider = dividers[index - 1];
          const nextDivider = dividers[index + 1];
          if (prevDivider === undefined || nextDivider === undefined) return;
          lo = prevDivider + MIN_GRID_GAP;
          hi = nextDivider - MIN_GRID_GAP;
        }
        const snapped = snapAxis(readAxis(node), isCol ? 'x' : 'y');
        const clamped = clamp(lo, hi, snapped);
        if (clamped !== readAxis(node)) writeAxis(node, clamped);
        if (individual) {
          const next = [...dividers];
          next[index] = Math.round(clamped);
          setDividers(next);
        }
        // Non-individual: don't dispatch on every move — index would shift mid-drag.
      }}
      onDragEnd={(e) => {
        if (!individual && startRef.current) {
          const { start, span } = startRef.current;
          const dropped = snapAxis(readAxis(e.target), isCol ? 'x' : 'y');
          const count = uniformCountFromDrag(start, span, index, dropped);
          setDividers(uniformDividers(start, span, count));
        }
        startRef.current = null;
        useStore.temporal.getState().resume();
      }}
    />
  );
}

export function GridOverlay() {
  const bounds = useStore(selectGridBounds);
  const cols = useColDividers();
  const rows = useRowDividers();
  const drawMode = useStore((s) => s.editor.drawMode);
  const selectedBoxes = useStore(useShallow((s) => s.editor.selectedBoxes));
  const cropRect = useStore((s) => s.grid.cropRect);
  const cellRects = useMemo(
    () => getCellRects({ cropRect, rowDividers: rows, colDividers: cols }),
    [cropRect, rows, cols],
  );
  const setSelectedBoxes = useStore((s) => s.setSelectedBoxes);
  const toggleBoxSelection = useStore((s) => s.toggleBoxSelection);

  if (!bounds || cols.length < 2 || rows.length < 2) return null;

  const selectedSet = new Set(selectedBoxes.map((b) => `${b.row},${b.col}`));

  return (
    <>
      {/* Hit-test cells — first in JSX so they render at the BOTTOM of the layer.
          Konva traverses children in REVERSE order for hit detection, so dividers
          (later siblings) capture clicks first near edges. */}
      {cellRects.map(({ box, rect }) => (
        <Rect
          key={`cell-${box.row}-${box.col}`}
          x={rect.x}
          y={rect.y}
          width={rect.w}
          height={rect.h}
          fill="transparent"
          listening={drawMode === 'none'}
          onMouseEnter={(e) => setStageCursor(e, 'pointer')}
          onMouseLeave={(e) => setStageCursor(e, '')}
          onClick={(e) => {
            if (e.evt.shiftKey) {
              toggleBoxSelection(box);
            } else {
              setSelectedBoxes([box]);
            }
          }}
          onTap={() => setSelectedBoxes([box])}
        />
      ))}
      {/* crop edges + dividers (existing — listening) */}
      <CropEdge side="left" />
      <CropEdge side="right" />
      <CropEdge side="top" />
      <CropEdge side="bottom" />
      {Array.from({ length: cols.length - 2 }, (_, k) => (
        <Divider
          // biome-ignore lint/suspicious/noArrayIndexKey: structural position, not reorder-sensitive
          key={`col-${k + 1}`}
          axis="x"
          index={k + 1}
          spanMin={bounds.y}
          spanMax={bounds.y + bounds.h}
        />
      ))}
      {Array.from({ length: rows.length - 2 }, (_, k) => (
        <Divider
          // biome-ignore lint/suspicious/noArrayIndexKey: structural position, not reorder-sensitive
          key={`row-${k + 1}`}
          axis="y"
          index={k + 1}
          spanMin={bounds.x}
          spanMax={bounds.x + bounds.w}
        />
      ))}
      {/* selected outlines (front, non-listening) */}
      {cellRects
        .filter(({ box }) => selectedSet.has(`${box.row},${box.col}`))
        .map(({ box, rect }) => (
          <Rect
            key={`sel-${box.row}-${box.col}`}
            x={rect.x}
            y={rect.y}
            width={rect.w}
            height={rect.h}
            stroke={GRID_SEL_COLOR}
            strokeWidth={2}
            strokeScaleEnabled={false}
            listening={false}
          />
        ))}
    </>
  );
}
